import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Cube, MagnifyingGlass } from "@phosphor-icons/react";
import { inr } from "./CostSummary";

/** BOM lines cross-linked with the 3D model: pick a line to highlight the part,
 *  pick a part to filter the lines that build it. */
export default function BomModelLink({ projectId }) {
  const [data, setData] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [q, setQ] = useState("");
  const [onlyLinked, setOnlyLinked] = useState(false);
  const rowRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/bom/${projectId}/model-links`);
      setData(d);
    } catch (e) { toast.error(fmtErr(e)); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => {
    let out = data?.items || [];
    if (onlyLinked) out = out.filter((i) => i.component_key);
    if (highlight) out = out.filter((i) => i.component_key === highlight);
    if (q) {
      const lq = q.toLowerCase();
      out = out.filter((i) => (i.description || "").toLowerCase().includes(lq) || i.item_number.includes(lq));
    }
    return out;
  }, [data, q, onlyLinked, highlight]);

  useEffect(() => {
    if (highlight && rowRefs.current[highlight]) {
      rowRefs.current[highlight].scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlight]);

  if (!data) return <div className="text-[11px] text-[var(--c-muted)] p-6" data-testid="bom-model-loading">Loading…</div>;

  const comp = (data.components || []).find((c) => c.key === highlight);

  return (
    <div className="space-y-3" data-testid="bom-model-link">
      <div className="flex flex-wrap items-center gap-4 rounded-sm border border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2.5 font-mono text-[11px]">
        <span className="flex items-center gap-1.5 text-[var(--c-text)]"><Cube size={13} /> 3D-linked BOM</span>
        <span className="text-[var(--c-green)]" data-testid="bom-linked-count">
          {data.linked_count} of {data.total_items} lines linked to a modelled part
        </span>
        <span className="text-[var(--c-muted)]">{(data.components || []).length} parts in the model</span>
        {highlight && (
          <button data-testid="bom-clear-part" onClick={() => setHighlight(null)}
            className="ml-auto text-[var(--c-accent)] hover:underline">Clear part filter</button>
        )}
      </div>

      {!data.has_model && (
        <div className="bg-[var(--c-amber)]/10 border border-[var(--c-amber)]/40 rounded-sm p-3 text-[11px] text-[var(--c-text)]"
          data-testid="bom-no-model">
          No 3D model on this project yet — generate it from the Studio tab (3D Model) and the BOM lines
          will cross-link to its parts automatically.
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] items-start">
        <div className="relative h-[30rem] rounded-lg border border-[var(--c-border)] overflow-hidden bg-[var(--c-bg)] grid place-items-center p-6 text-center"
          data-testid="bom-model-viewer">
          <p className="text-[11px] text-[var(--c-muted)] max-w-[34ch]">
            The 3D preview moves to the spec-driven Model Engine. Part ↔ BOM linking in the new
            viewer lands with the transformer rule pack (Phase 2); the cross-link table is live below.
          </p>
        </div>

        <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] overflow-hidden">
          <div className="flex items-center gap-2 p-2 border-b border-[var(--c-border)]">
            <div className="relative flex-1">
              <MagnifyingGlass size={12} className="absolute left-2 top-2 text-[var(--c-muted)]" />
              <input data-testid="bom-model-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search BOM lines…"
                className="w-full h-7 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm pl-7 pr-2 outline-none focus:border-[var(--c-accent)]" />
            </div>
            <label className="flex items-center gap-1.5 text-[10px] text-[var(--c-muted)] cursor-pointer">
              <input type="checkbox" className="accent-[var(--c-accent)]" checked={onlyLinked}
                data-testid="bom-only-linked" onChange={(e) => setOnlyLinked(e.target.checked)} />
              linked only
            </label>
          </div>
          {comp && (
            <div className="px-3 py-2 bg-[var(--c-accent)]/10 border-b border-[var(--c-border)] text-[11px] text-[var(--c-text)]"
              data-testid="bom-selected-part">
              Item {comp.no} · {comp.desc}
              <span className="text-[10px] text-[var(--c-muted)] font-mono ml-2">
                {comp.items.length} BOM line(s){comp.material ? ` · ${comp.material}` : ""}
                {comp.mass_kg != null ? ` · ${comp.mass_kg} kg` : ""}
              </span>
            </div>
          )}
          <div className="max-h-[26rem] overflow-y-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--c-border)] text-left font-mono text-[9px] uppercase tracking-wider text-[var(--c-muted)]">
                  <th className="py-2 px-2">Item</th><th className="py-2 px-2">Description</th>
                  <th className="py-2 px-2 text-right">Qty</th><th className="py-2 px-2">3D part</th>
                  <th className="py-2 px-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.item_number} data-testid={`bom-link-row-${i.item_number}`}
                    ref={(el) => { if (i.component_key) rowRefs.current[i.component_key] = el; }}
                    onClick={() => i.component_key && setHighlight((h) => (h === i.component_key ? null : i.component_key))}
                    className={`border-b border-[var(--c-border)]/40 cursor-pointer transition-colors duration-100 ${
                      i.component_key === highlight ? "bg-[var(--c-amber)]/15" : "hover:bg-[var(--c-border)]/25"}`}>
                    <td className="py-1.5 px-2 font-mono text-[10px] text-[var(--c-muted)]">{i.item_number}</td>
                    <td className="py-1.5 px-2 text-[11px] text-[var(--c-text)]">{i.description}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-[11px] text-[var(--c-text)]">{i.quantity} {i.unit}</td>
                    <td className="py-1.5 px-2 text-[10px]">
                      {i.component_key ? (
                        <span className="text-[var(--c-green)] font-mono">
                          {(data.components.find((c) => c.key === i.component_key) || {}).no ?? "—"} · {i.component_key}
                        </span>
                      ) : (
                        <span className="text-[var(--c-muted)]">not modelled</span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono text-[11px] text-[var(--c-muted)]">{inr(i.total_cost)}</td>
                  </tr>
                ))}
                {!items.length && (
                  <tr><td colSpan={5} className="py-8 text-center text-[11px] text-[var(--c-muted)]">
                    No BOM lines match.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
